import { html, type TemplateResult } from "lit";
import { Engine } from "../../engine/Engine";
import { BaseElement } from "../ui-kit/BaseElement";
import {
  createRenderSettingsHostBinding,
  type RenderSettingsHostBinding,
} from "../pipeline/bindRenderSettingsHost";
import type { RenderSettingsProductScope } from "../pipeline/RenderSettingsStore";
import type { SugarViewport } from "../viewport/SugarCanvas";
import {
  renderCapabilities,
  type CapabilityProps,
  type ViewportCapability,
} from "./viewportCapabilities";

export interface ProductViewportOptions {
  /** `<sugar-viewport no-environment>` */
  noEnvironment?: boolean;
  /** `<sugar-viewport no-default-camera>` */
  noDefaultCamera?: boolean;
  /** Viewport'un kök elementine verilecek class. */
  class?: string;
}

/**
 * Bütün Sugar ürün elementlerinin ortak tabanı.
 *
 * Bir ürün elementi (sugar-model-viewer, sugar-configurator,
 * sugar-room-designer ve bundan sonra yazılacak olanlar) hep aynı üç şeyi
 * kurmak zorundaydı ve üçünü de kendi içinde elle yazıyordu:
 *
 *  1. render-settings bağlantısı (pipeline ayarlarının viewport'a uygulanması)
 *     — `connectedCallback`'te connect, `disconnectedCallback`'te disconnect
 *  2. `<sugar-viewport>` ve içindeki yetenek elementleri (orbit, transformer,
 *     post-process'ler) — her şablonda kopyalanmış etiket listesi
 *  3. engine / viewport erişimi
 *
 * Bu sınıf üçünü de üstlenir. Ürün yalnızca şunu söyler:
 *
 * ```ts
 * @customElement("sugar-yeni-urun")
 * export class SugarYeniUrun extends SugarProductElement {
 *   protected override capabilities = PRODUCT_VIEWER_CAPABILITIES;
 *   protected override viewportOptions = { noEnvironment: true };
 *
 *   protected renderHtml() {
 *     return html`
 *       <div class="layout">
 *         ${this.renderViewport()}
 *         <my-own-panel></my-own-panel>
 *       </div>
 *     `;
 *   }
 * }
 * ```
 *
 * UI ve controller ürüne aittir; bu sınıf onlara karışmaz.
 */
export abstract class SugarProductElement extends BaseElement {
  /** Viewport'a yerleştirilecek yetenekler. Ürün ezer. */
  protected capabilities: readonly ViewportCapability[] = [];

  /** `<sugar-viewport>` attribute'ları. Ürün ezer. */
  protected viewportOptions: ProductViewportOptions = {};

  /** Isolated settings defaults + localStorage bag. Ürün ezer. */
  protected renderSettingsScope: RenderSettingsProductScope = "shared";

  /**
   * Yeteneklere geçilecek property'ler (ör. `product-viewer` için `.data`).
   * Her render'da okunur.
   */
  protected capabilityProps(): CapabilityProps | undefined {
    return undefined;
  }

  private _renderSettings?: RenderSettingsHostBinding;

  /** Pipeline ayarlarını elle yeniden uygulamak gerekirse. */
  protected get renderSettings(): RenderSettingsHostBinding {
    if (!this._renderSettings) {
      this._renderSettings = createRenderSettingsHostBinding({
        eventTarget: this,
        resolveApplyHost: () =>
          this.renderRoot.querySelector("sugar-viewport") ?? this.renderRoot,
        resolveEngine: () => Engine.activeEngine ?? undefined,
        productScope: this.renderSettingsScope,
      });
    }
    return this._renderSettings;
  }

  get viewport(): SugarViewport | undefined {
    return (
      (this.renderRoot?.querySelector("sugar-viewport") as SugarViewport) ??
      undefined
    );
  }

  get engine(): Engine | undefined {
    return this.viewport?.engine ?? Engine.activeEngine ?? undefined;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.renderSettings.connect();
  }

  override disconnectedCallback(): void {
    this._renderSettings?.disconnect();
    super.disconnectedCallback();
  }

  /**
   * Ürünün şablonunda çağrılır. `extra` ile ürüne özel ek element konabilir
   * (ör. configurator'ün `<configurator-session>`'ı).
   */
  protected renderViewport(extra?: TemplateResult | typeof html): TemplateResult {
    const o = this.viewportOptions;
    return html`
      <sugar-viewport
        class=${o.class ?? ""}
        ?no-environment=${!!o.noEnvironment}
        ?no-default-camera=${!!o.noDefaultCamera}
      >
        ${extra ?? ""}
        ${renderCapabilities(this.capabilities, this.capabilityProps())}
      </sugar-viewport>
    `;
  }
}
